import { useEffect, useRef } from "react";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export async function toggleFullscreen(): Promise<void> {
  try {
    if (window.gameDesktop) { await window.gameDesktop.toggleFullscreen(); return; }
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch { /* browser or host may deny fullscreen */ }
}

function entries(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => element.offsetParent !== null && getComputedStyle(element).visibility !== "hidden");
}

function move(delta: number): void {
  const list = entries();
  if (!list.length) return;
  const index = Math.max(0, list.indexOf(document.activeElement as HTMLElement));
  list[(index + delta + list.length) % list.length].focus();
}

export function useGamepadNavigation(onBack: () => void, enabled = true): void {
  const previous = useRef<boolean[]>([]);
  const lastMove = useRef(0);
  useEffect(() => {
    if (!enabled || typeof navigator.getGamepads !== "function") return;
    let frame = 0;
    const tick = (time: number) => {
      const pad = [...navigator.getGamepads()].find(Boolean);
      if (pad) {
        const pressed = pad.buttons.map((button) => button.pressed);
        const edge = (index: number) => pressed[index] && !previous.current[index];
        if (edge(0)) (document.activeElement as HTMLElement | null)?.click();
        if (edge(1) || edge(9)) onBack();
        if (time - lastMove.current > 180) {
          const axis = Math.abs(pad.axes[1] ?? 0) > Math.abs(pad.axes[0] ?? 0) ? pad.axes[1] : pad.axes[0];
          if (edge(12) || edge(14) || axis < -0.65) { move(-1); lastMove.current = time; }
          if (edge(13) || edge(15) || axis > 0.65) { move(1); lastMove.current = time; }
        }
        previous.current = pressed;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [enabled, onBack]);
}
